/**
 * OfferHero.tsx
 *
 * File-level:
 * - Header-level visual hero for promotions. Shows a large splashy promotion pill that sits beside the page header.
 *
 * Notes:
 * - Accepts an onDismiss callback to close/hide the hero (useful to persist dismissal).
 * - Designed to be accessible and responsive.
 */

import React from 'react';
import './OfferBadge.css';
import { X } from 'lucide-react';

interface OfferHeroProps {
  /** Promotional text to display */
  text?: string;
  /** Optional callback invoked when user dismisses the hero */
  onDismiss?: () => void;
}

/**
 * OfferHero
 *
 * @description Large header-level promo badge with shimmer and sparkles. If onDismiss is provided,
 * renders a close button and calls it when clicked.
 */
export default function OfferHero({ text = 'New Year Dhamaka — 50% OFF on all plans', onDismiss }: OfferHeroProps): JSX.Element {
  return (
    <div className="offer-hero" role="status" aria-live="polite" aria-label="Promotional offer">
      <div className="offer-hero-pill">50% OFF</div>
      <div className="offer-hero-title">{text}</div>

      {/* Decorative sparkles */}
      <span className="offer-sparkle" data-index="1" aria-hidden />
      <span className="offer-sparkle" data-index="2" aria-hidden />

      {onDismiss ? (
        <button
          type="button"
          className="hero-close"
          aria-label="Dismiss promotional offer"
          onClick={onDismiss}
        >
          <X className="w-4 h-4 text-white" />
        </button>
      ) : null}
    </div>
  );
}